/* -----------------------------------------------------------------------
 * <copyright company="Microsoft Corporation">
 *   Copyright (c) Microsoft Corporation.  All rights reserved.
 * </copyright>
 * ----------------------------------------------------------------------- */

import "./UploadOrDecline.scss";

import { ActionType, EventName } from "@microsoftit/telemetry-extensions-npm";
import { ApplicationStatus, TelemetryLogs } from "../constants";
import { DefaultButton, PrimaryButton } from "@fluentui/react";

import { DeclineDialog } from "./dialogs/decline";
import { DeclineSuccessSVG } from "../assets/decline-success";
import React from "react";
import { UploadDialog } from "./dialogs/upload";
import { UploadSuccessSVG } from "../assets/upload-success";
import { logTelemetry } from "../../services/telemetry/telemetryWrapper";
import { useBoolean } from "@fluentui/react-hooks";
import floodgateService from "../../services/survey/FloodgateService";

interface Props {
  candidateFirstName: string;
  candidateLastName: string;
  updatedDate: string;
  status: ApplicationStatus;
  setAppStatus: (status: ApplicationStatus) => void;
}
export const UploadOrDecline: React.FC<Props> = ({
  candidateFirstName,
  candidateLastName,
  updatedDate,
  status,
  setAppStatus,
}) => {
  const [hideDeclineDialog, { toggle: toggleDeclineDialog }] = useBoolean(true);
  const [hideUploadDialog, { toggle: toggleUploadDialog }] = useBoolean(true);

  React.useEffect(() => {
    if (status === ApplicationStatus.REQUESTED) {
      logTelemetry(
        TelemetryLogs.PageViews,
        TelemetryLogs.PageViews,
        ActionType.User,
        TelemetryLogs.PageViews,
        EventName.PageLoad
      );
    }
  }, [status]);
  React.useEffect(() => {
    floodgateService.init();
  },[]);

  return (
    <div
      className="ms-ac-referenceview-welcome-screen"
      data-test-id="uploadDeclineTest"
    >
      {status === ApplicationStatus.REQUESTED && (
        <div>
          <p className="ms-ac-referenceview-upload-header">Provide a letter</p>
          <div className="ms-ac-referenceview-flex-con">
            <p className="ms-ac-referenceview-upload-txt">
              To upload a letter of reference for {candidateFirstName}, select
              upload. The candidate won&apos;t have access to your letter.
            </p>
            <PrimaryButton
              iconProps={{ iconName: "TextDocument" }}
              onClick={toggleUploadDialog}
              className="ms-ac-referenceview-upload-btn"
              text="Upload reference letter"
              allowDisabledFocus
              disabled={false}
            />
          </div>
          <p className="ms-ac-referenceview-decline-header">OR</p>
          <div className="ms-ac-referenceview-flex-con">
            <p className="ms-ac-referenceview-upload-txt">
              If you don&apos;t want to provide a reference letter now, select
              decline. We&apos;ll notify {candidateFirstName} that you&apos;ve
              declined so they can choose another person to support their
              application.
            </p>
            <DefaultButton
              className="ms-ac-referenceview-decline-btn"
              iconProps={{ iconName: "Cancel" }}
              text="Decline request"
              onClick={toggleDeclineDialog}
              allowDisabledFocus
              disabled={false}
            />
          </div>
        </div>
      )}
      {(status === ApplicationStatus.APPROVED ||
        status === ApplicationStatus.PENDING) && (
        <div className="ms-ac-referenceview-upload-success-con">
          <div className="ms-ac-referenceview-upload-success-header">
            <UploadSuccessSVG></UploadSuccessSVG>
            <p className="ms-ac-referenceview-upload-success-text">
              You provided a reference letter for {candidateFirstName}{" "}
              {candidateLastName} on {updatedDate}. Should you wish to provide
              an updated letter, select upload.
            </p>
          </div>
          <div className="ms-ac-referenceview-upload-success-btn">
            <PrimaryButton
              iconProps={{ iconName: "TextDocument" }}
              onClick={toggleUploadDialog}
              className="ms-ac-referenceview-upload-btn"
              text="Upload new reference letter"
              allowDisabledFocus
              disabled={false}
            />
          </div>
        </div>
      )}
      {status === ApplicationStatus.DECLINED && (
        <div className="ms-ac-referenceview-upload-success-con">
          <div className="ms-ac-referenceview-upload-success-header">
            <DeclineSuccessSVG></DeclineSuccessSVG>
            <p className="ms-ac-referenceview-upload-success-text">
              You declined this request for {candidateFirstName}{" "}
              {candidateLastName} on {updatedDate}. Should you still wish to
              provide a letter of reference, select upload.
            </p>
          </div>
          <div className="ms-ac-referenceview-upload-decline-btn">
            <PrimaryButton
              iconProps={{ iconName: "TextDocument" }}
              onClick={toggleUploadDialog}
              className="ms-ac-referenceview-upload-btn"
              text="Upload reference letter"
              allowDisabledFocus
              disabled={false}
            />
          </div>
        </div>
      )}

      <UploadDialog
        hideUploadDialog={hideUploadDialog}
        toggleUploadDialog={toggleUploadDialog}
        setStatus={setAppStatus}
        status={status}
      ></UploadDialog>
      <DeclineDialog
        hideDeclineDialog={hideDeclineDialog}
        toggleDeclineDialog={toggleDeclineDialog}
        setStatus={setAppStatus}
        candidateFirstName={candidateFirstName}
      ></DeclineDialog>
    </div>
  );
};
